import React, { useEffect, useState } from 'react';
import { HelixLoader, PageWrapper, UpdatePageHeader } from 'components';
import { CONSENT_CONTRACT_PATHS } from 'constants/consent-contracts-paths';
import { useAlert, useAuth } from '../../../core';
import {
    useDeleteConsentContract,
    useDictionaryDetails,
    useSdo,
    useUpdateConsentContract,
    useUserGroupList,
    useWalletList,
} from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
    consentContractStyles,
    getCombinedOptionsData,
    JWTPropertiesErrorMessage,
    updateConsentContractDefaults,
    UpdateConsentContractFormData,
    UpdateConsentContractSchema,
} from './create-or-update-consent-contract-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Typography } from '@mui/material';
import { getAvailableAttributeList, validationsMessages } from 'lib';
import { ConsentContractForm } from './ConsentContractForm';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isInvalidDate } from '../consent-contract-helper';
dayjs.extend(utc);

export const UpdateConsentContract = (): React.JSX.Element => {
    const { getToken, getUserEmail, getUserName } = useAuth();
    const navigate = useNavigate();
    const { sdoId, chain } = useParams();
    const { showAlert, hideAlert } = useAlert();
    const { data: consentContractData, isLoading: consentContractDataLoading } =
        useSdo({
            chain: chain,
            sdoId: sdoId,
        });
    const { mutate: deleteConsentContract } = useDeleteConsentContract({
        chain: chain,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [optionsData, setOptionsData] = useState<any[]>([]);
    const [jwtPropertiesValue, setJwtPropertiesValue] = useState<string>();
    const [attributeListAsString, setAttributeListAsString] = useState<
        string[]
    >([]);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [chipColor, setChipColor] = useState<
        'primary' | 'secondary' | 'grayscale'
    >('primary');
    const [isExpireContractButtonDisable, setIsExpireContractButtonDisable] =
        useState<boolean>(false);
    const [chipLabel, setChipLabel] = useState<
        'Scheduled' | 'Active' | 'Expired'
    >('Active');
    const [expireEndDate, setExpireEndDate] = useState<any>();
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const { data: userGroupsData, isLoading: userGroupsLoading } =
        useUserGroupList();
    const onSuccess = (data: any): void => {
        setAttributeListAsString(getAvailableAttributeList(data?.attributes));
    };
    const { mutate: updateConsentContract } = useUpdateConsentContract();
    useDictionaryDetails({
        dictionaryId: chain,
        onLoadSuccess: onSuccess,
    });
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<UpdateConsentContractFormData>({
        mode: 'all',
        resolver: yupResolver(UpdateConsentContractSchema),
        defaultValues: updateConsentContractDefaults,
    });

    const handleChipStatus = (startDate: any, endDate: any): void => {
        const currentDate = new Date();
        if (!isInvalidDate(endDate) && endDate < currentDate) {
            // Check if expired
            setChipColor('grayscale');
            setChipLabel('Expired');
            setIsExpireContractButtonDisable(true);
        } else if (!isInvalidDate(startDate) && currentDate < startDate) {
            // Check if scheduled
            setChipColor('secondary');
            setChipLabel('Scheduled');
            setIsExpireContractButtonDisable(false);
        } else {
            // If active
            setChipColor('primary');
            setChipLabel('Active');
            setIsExpireContractButtonDisable(false);
        }
    };

    useEffect(() => {
        const startDate = new Date(dayjs(watch('start'))?.format());
        const endDate = new Date(dayjs(watch('until'))?.format());
        if (!isInvalidDate(startDate) || !isInvalidDate(endDate)) {
            handleChipStatus(startDate, endDate);
        } else {
            setChipColor('primary');
            setChipLabel('Active');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch('start'), watch('until')]);

    const handleToFieldData = (
        contract: any,
        combinedOptionsData: any
    ): any => {
        if (contract?.to) {
            if (contract.to.length && contract.to[0] === '*') {
                setValue('to', combinedOptionsData);
            } else {
                const toFieldData = contract.to
                    .map((id: any) => {
                        return combinedOptionsData.find(
                            (option: any) => id === option.id
                        );
                    })
                    .filter(Boolean);
                setValue('to', toFieldData.length > 0 ? toFieldData : []);
            }
            setValue('share', 'to');
        }
    };

    const handleJwtProperties = (contract: any): any => {
        if (contract?.jwtProperties) {
            setJwtPropertiesValue(contract.jwtProperties);
            setValue('jwtProperties', contract.jwtProperties);
            setValue('share', 'jwtProperties');
        }
    };

    const handleConsentContractData = (combinedOptionsData: any): any => {
        const contract = consentContractData?.consentContract;

        setValue('description', contract?.description ?? '');
        setValue('when', contract?.when ?? '');
        setValue('only', contract?.only ?? []);

        setValue('start', contract?.start ? dayjs(contract.start) : null);
        setValue('until', contract?.until ? dayjs(contract.until) : null);

        handleToFieldData(contract, combinedOptionsData);
        handleJwtProperties(contract);
    };

    useEffect(() => {
        const userEmail = getUserEmail();
        const userName = getUserName();
        let combinedOptionsData: any[] = [];
        if (!walletsLoading && !userGroupsLoading) {
            combinedOptionsData = getCombinedOptionsData(
                walletsData,
                userGroupsData,
                userEmail,
                userName
            );
            setOptionsData(combinedOptionsData);
        }
        if (!consentContractDataLoading) {
            handleConsentContractData(combinedOptionsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walletsLoading, userGroupsLoading, consentContractDataLoading]);

    const onSaveButtonClick = (data: UpdateConsentContractFormData): void => {
        if (
            watch('share') !== 'to' &&
            !Object.keys(data.jwtProperties).length
        ) {
            setError('jwtProperties', {
                message: validationsMessages.required,
            });
            return;
        }
        if (watch('start') && watch('until')) {
            const startDate = new Date(dayjs(watch('start')).format());
            const endDate = new Date(dayjs(watch('until')).format());

            if (endDate < startDate) {
                showAlert({
                    severity: 'error',
                    variant: 'filled',
                    closable: true,
                    onClose: hideAlert,
                    children: 'End date occurs before start date.',
                });
                return;
            }
        }
        setIsChangesUnsaved(false);
        setIsLoading(true);
        updateConsentContract(
            {
                id: sdoId as string,
                contract: {
                    consentContract: {
                        ...(watch('share') === 'to'
                            ? {
                                  to:
                                      watch('to').length === optionsData.length
                                          ? '*'
                                          : data.to.map((item: any) => item.id),
                              }
                            : {
                                  jwtProperties: data.jwtProperties,
                              }),
                        description: data.description,
                        only: data.only,
                        start:
                            data.start === null
                                ? ''
                                : dayjs(data.start)
                                      .utc()
                                      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                        until:
                            data.until === null
                                ? ''
                                : dayjs(data.until)
                                      .utc()
                                      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
                        when: data.when,
                    },
                },
                chain: chain as string,
                token: getToken(),
            },
            {
                onSuccess: (): void => {
                    navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, {
                        state: chain,
                    });
                    setIsLoading(false);
                },
                onError: (): void => {
                    setIsLoading(false);
                    setIsChangesUnsaved(true);
                },
            }
        );
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, {
                state: chain,
            });
        }
    };

    const handleConfirmation = (): void => {
        setTimeout(() => {
            navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, {
                state: chain,
            });
        }, 500);
        deleteConsentContract({
            id: sdoId as string,
            chain: chain as string,
            token: getToken(),
        });
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, {
            state: chain,
        });
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleExpireContract = (): void => {
        setIsChangesUnsaved(true);
        const currentDate = new Date();
        setValue('until', dayjs(currentDate));
        setExpireEndDate(currentDate);
        setIsExpireContractButtonDisable(true);
    };

    const handleExpireButton = (): void => {
        const endDate = new Date(dayjs(watch('until'))?.format());
        if (expireEndDate) {
            if (endDate === expireEndDate) {
                setIsExpireContractButtonDisable(true);
            } else {
                setIsExpireContractButtonDisable(false);
            }
        }
    };

    const onJwtPropertiesValueChange = React.useCallback((value: any) => {
        setIsChangesUnsaved(true);
        try {
            setJwtPropertiesValue(JSON.parse(value));
            setValue('jwtProperties', JSON.parse(value));
            clearErrors('jwtProperties');
        } catch {
            setError('jwtProperties', {
                message: JWTPropertiesErrorMessage,
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (consentContractDataLoading || walletsLoading || userGroupsLoading) {
        return <HelixLoader />;
    }

    return (
        <PageWrapper
            title={'Edit Consent Contract'}
            includeBreadcrumbs={true}
            breadCrumStatus='update'
            breadCrumType='Consent_Contract'
            breadCrumData={chain}
            includeStatusChip={true}
            chipColor={chipColor}
            chipSize={'small'}
            chipLabel={chipLabel}
        >
            <UpdatePageHeader
                headingText={'General Info'}
                deleteMenuItemText={'Delete Contract'}
                handleSaveButton={handleSubmit(onSaveButtonClick)}
                handleCancelButton={handleCancelButton}
                isLoading={isLoading}
                isChangesUnsaved={isChangesUnsaved}
                openCancelModal={openCancelModal}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                handleCancelModal={handleCancelModal}
                unsavedChangesConfirmationText={
                    'This consent contract has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
                }
                confirmationModalConfig={{
                    confirmationButtonText: 'Yes, Delete',
                    confirmationHeadingText:
                        'Confirm Consent Contract Deletion',
                    handleConfirmation: handleConfirmation,
                    confirmationCancelButtonText: 'No, Cancel',
                    hasConfirmationTextField: false,
                    confirmationSubHeadingText: (
                        <>
                            Are you sure you would like to delete your consent
                            contract?
                        </>
                    ),
                }}
                menuWidth={'202px'}
            />
            <Grid sx={consentContractStyles.updateContainerStyles}>
                <Grid>
                    <Typography sx={consentContractStyles.shareLabelStyles}>
                        Shared
                    </Typography>
                    <Typography sx={consentContractStyles.shareValueStyles}>
                        {watch('share') === 'to'
                            ? 'To User(s)'
                            : 'JWT Properties'}
                    </Typography>
                </Grid>
                <ConsentContractForm
                    isEdit={true}
                    control={control}
                    errors={errors}
                    watch={watch}
                    optionsData={optionsData}
                    setValue={setValue}
                    jwtPropertiesValue={JSON.stringify(jwtPropertiesValue)}
                    onJwtPropertiesValueChange={onJwtPropertiesValueChange}
                    attributeListAsString={attributeListAsString}
                    disablePastStartDate={false}
                    disablePastEndDate={false}
                    handleUnsavedChanges={(): void => {
                        setIsChangesUnsaved(true);
                    }}
                    isExpireContractButtonDisable={
                        isExpireContractButtonDisable
                    }
                    handleExpireContract={handleExpireContract}
                    handleExpireButton={handleExpireButton}
                />
            </Grid>
        </PageWrapper>
    );
};
